import { useNavigate, useSearchParams } from "react-router-dom";
import DataInput from "@/atoms/dataInput";
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  Typography,
} from "@/common/styles/style";
import { styles } from "./style";
import { Routes } from "@/router/routes";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import RequiredAsterisk from "@/atoms/requiredAsterisk";
import { FormEvent, useState } from "react";
import { sanitizePasswordInput } from "@/helpers/sanitizePasswordInput";
import ErrorMessage from "@/atoms/errorMessage";

export default function PasswordReset() {
  const navigate = useNavigate();
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formValues, setFormValues] = useState({
    password: "",
    confirmPw: "",
  });
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const inputList = [
    {
      label: "パスワード",
      field: "password",
      type: showPassword ? "text" : "password",
      InputProps: {
        endAdornment: (
          <InputAdornment position="end">
            <IconButton
              onClick={() => setShowPassword((prev) => !prev)}
              edge="end"
            >
              {showPassword ? <VisibilityOff /> : <Visibility />}
            </IconButton>
          </InputAdornment>
        ),
      },
    },
    {
      label: "パスワード (確認用)",
      field: "confirmPw",
      type: showConfirmPassword ? "text" : "password",
      InputProps: {
        endAdornment: (
          <InputAdornment position="end">
            <IconButton
              onClick={() => setShowConfirmPassword((prev) => !prev)}
              edge="end"
            >
              {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
            </IconButton>
          </InputAdornment>
        ),
      },
    },
  ];

  const handleChange = (field: keyof typeof formValues, value: string) => {
    if (field === "password" || field === "confirmPw") {
      const sanitizedValue = sanitizePasswordInput(value, setError);
      setFormValues((prev) => ({ ...prev, [field]: sanitizedValue }));
    } else {
      setFormValues((prev) => ({ ...prev, [field]: value }));
    }
  };

  const validateForm = () => {
    const { password, confirmPw } = formValues;

    if (!password || !confirmPw) {
      setError("すべての項目を入力してください。");
      return false;
    }

    if (password !== confirmPw) {
      setError("パスワードが一致しません。");
      return false;
    }

    setError("");
    return true;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    if (!token) {
      setError("トークンがありません。もう一度お試しください。");
      return;
    }

    try {
      const response = await fetch(`/api/password/reset/confirm?token=${token}`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ password: formValues.password }),
        });

      const data = await response.json();
      if (response.ok) {
        console.log(data.message);
        setIsSubmitted(true);
      } else {
        setError(data.message);
      }
    } catch (error) {
      console.error("API呼び出しエラー:", error);
    }
  };

  const handleReturnLogin = () => {
    navigate(Routes.LOGIN);
  };

  return (
    <Box sx={styles.container}>
      {!isSubmitted ? (
        <Box sx={styles.box}>
          <Typography variant="h5" align="center" marginBottom={5}>
            パスワードリセット
          </Typography>
          <ErrorMessage error={error} />
          <form onSubmit={handleSubmit}>
            {inputList.map((input, index) => (
              <DataInput
                key={index}
                datatype="TextField"
                label={
                  <label style={styles.labelFlex}>
                    {input.label}
                    <RequiredAsterisk />
                  </label>
                }
                typographyProps={{
                  sx: styles.text,
                }}
                textProps={{
                  variant: "outlined",
                  type: input.type,
                  sx: styles.input,
                  value: formValues[input.field as keyof typeof formValues],
                  InputProps: input.InputProps,
                }}
                onChange={(value) =>
                  handleChange(input.field as keyof typeof formValues, value)
                }
              />
            ))}
            <Button variant="contained" sx={styles.button} type="submit">
              送信
            </Button>
          </form>
        </Box>
      ) : (
        <Box sx={styles.isSubmittedBox}>
          <Typography variant="h5" align="center" marginBottom={5}>
            パスワードリセット完了
          </Typography>
          <Typography align="center" marginBottom={5}>
            パスワード再設定が完了しました。
          </Typography>
          <Button
            variant="text"
            sx={styles.moveButton}
            onClick={handleReturnLogin}
          >
            ログイン画面へ戻る
          </Button>
        </Box>
      )}
    </Box>
  );
}
